<template>
  <div class="login">
    <div class="login__halo" aria-hidden="true"></div>

    <div class="login__inner">
      <div class="login__brand">
        <h1 class="login__wordmark">pmm</h1>
        <p class="login__tagline">Digital Ledger — Editorial Financial Intelligence</p>
      </div>

      <div class="al-card login__card">
        <!-- Invalid / missing token -->
        <template v-if="!token">
          <div class="reset__state">
            <span class="material-symbols-outlined reset__icon reset__icon--error">error</span>
            <h2 class="reset__title">Invalid link</h2>
            <p class="reset__subtitle">This reset link is missing or malformed.</p>
            <router-link to="/forgot-password" class="reset__back-link">Request a new link</router-link>
          </div>
        </template>

        <!-- Success state -->
        <template v-else-if="done">
          <div class="reset__state">
            <span class="material-symbols-outlined reset__icon">check_circle</span>
            <h2 class="reset__title">Password updated</h2>
            <p class="reset__subtitle">Your password has been reset. You can now log in.</p>
            <router-link to="/login" class="reset__back-link">Go to login</router-link>
          </div>
        </template>

        <!-- Reset form -->
        <template v-else>
          <div class="reset__header">
            <h2 class="reset__title">Set new password</h2>
            <p class="reset__subtitle">
              Choose a strong password — at least 12 characters with an uppercase letter,
              a digit, and a special character.
            </p>
          </div>

          <n-form @submit.prevent="handleSubmit">
            <n-form-item label="New password">
              <n-input
                v-model:value="form.password"
                type="password"
                show-password-on="click"
                placeholder="••••••••••••"
                size="large"
              />
            </n-form-item>
            <n-form-item label="Confirm password">
              <n-input
                v-model:value="form.confirmPassword"
                type="password"
                show-password-on="click"
                placeholder="••••••••••••"
                size="large"
                @keyup.enter="handleSubmit"
              />
            </n-form-item>
            <n-button
              type="primary"
              block
              size="large"
              :loading="authStore.loading"
              @click="handleSubmit"
            >
              Reset password
            </n-button>
          </n-form>

          <n-alert
            v-if="errorMessage"
            type="error"
            style="margin-top: 1rem"
            closable
            @close="errorMessage = null"
          >
            {{ errorMessage }}
          </n-alert>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { NForm, NFormItem, NInput, NButton, NAlert } from 'naive-ui';
import { useAuthStore } from '@/stores/authStore';

const route = useRoute();
const authStore = useAuthStore();

const token = route.query.token || '';
const done = ref(false);
const errorMessage = ref(null);
const form = ref({ password: '', confirmPassword: '' });

async function handleSubmit() {
  errorMessage.value = null;

  if (form.value.password !== form.value.confirmPassword) {
    errorMessage.value = 'Passwords do not match';
    return;
  }

  try {
    await authStore.resetPassword(token, form.value.password);
    done.value = true;
  } catch (error) {
    errorMessage.value = error.message;
  }
}
</script>

<style scoped>
.login {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  overflow: hidden;
  background: var(--al-background);
}

.login__halo {
  position: absolute;
  inset: -20%;
  background:
    radial-gradient(circle at 20% 30%, rgba(0, 229, 255, 0.14), transparent 55%),
    radial-gradient(circle at 80% 70%, rgba(195, 245, 255, 0.07), transparent 50%);
  filter: blur(40px);
  pointer-events: none;
}

.login__inner {
  position: relative;
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.login__brand {
  text-align: center;
}

.login__wordmark {
  font-family: var(--al-font-headline);
  font-weight: 800;
  font-size: 2.75rem;
  letter-spacing: 0.03em;
  color: var(--al-primary);
  margin: 0;
}

.login__tagline {
  color: var(--al-on-surface-variant);
  font-size: 0.8125rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  margin: 0.5rem 0 0;
}

.login__card {
  padding: 2rem;
}

.reset__header {
  margin-bottom: 1.5rem;
}

.reset__title {
  font-family: var(--al-font-headline);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0 0 0.5rem;
  color: var(--al-on-surface);
}

.reset__subtitle {
  font-size: 0.875rem;
  color: var(--al-on-surface-variant);
  margin: 0;
  line-height: 1.5;
}

.reset__state {
  text-align: center;
}

.reset__icon {
  font-size: 3rem;
  color: var(--al-primary);
}

.reset__icon--error {
  color: var(--n-color-error, #e88080);
}

.reset__back-link {
  display: inline-block;
  margin-top: 1.25rem;
  font-size: 0.875rem;
  color: var(--al-primary);
  text-decoration: none;
}

.reset__back-link:hover {
  text-decoration: underline;
}
</style>
